<template>
  <WelfareShell active="welfare-rules" subtitle="ขั้นที่ 1 — กำหนดว่าคำตอบข้อไหนในแบบสอบถามถือเป็นปัญหาในมิติใด (ตั้งครั้งเดียว แก้ไม่บ่อย) ส่วนสวัสดิการของแต่ละปัญหาจัดการแยกในขั้นที่ 2">
    <template #actions>
      <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="load"><RefreshCwIcon class="w-4 h-4 mr-1" /> โหลดใหม่</button>
      <button type="button" class="btn btn-primary bg-primary-btn border-primary-btn" @click="openCreate"><PlusIcon class="w-4 h-4 mr-1" /> เพิ่มปัญหา</button>
    </template>

    <!-- 5 dimensions = the main way to find a problem -->
    <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-4">
      <button type="button" class="dim-card" :class="{ 'dim-card--on': !fDim }" style="--dim:#415A77" @click="fDim = ''">
        <span class="dim-card-count">{{ kindRules.length }}</span>
        <span class="dim-card-label">ทุกมิติ</span>
      </button>
      <button v-for="d in DIMENSIONS" :key="d" type="button" class="dim-card" :class="{ 'dim-card--on': fDim === d }" :style="{ '--dim': DIMENSION_STYLE[d].color, '--dim-bg': DIMENSION_STYLE[d].background }" @click="fDim = fDim === d ? '' : d">
        <span class="dim-card-count">{{ dimCount[d] || 0 }}</span>
        <span class="dim-card-label">{{ d }}</span>
      </button>
    </div>

    <div class="box p-4">
      <div class="flex flex-col lg:flex-row lg:items-center gap-2 mb-3">
        <div class="seg flex-shrink-0 self-start" role="group" aria-label="ประเภท">
          <button v-for="k in KINDS" :key="k.value" type="button" :class="{ 'seg--on': fKind === k.value }" :title="k.help" @click="fKind = k.value">{{ k.label }} <span class="opacity-70">{{ kindCount[k.value] }}</span></button>
        </div>
        <div class="relative flex-1">
          <SearchIcon class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-theme-37" />
          <input v-model.trim="q" type="text" class="form-control pl-9" placeholder="ค้นหาชื่อปัญหา คำถาม หรือรหัส">
        </div>
        <select v-model="fLevel" class="form-select lg:w-40" aria-label="ระดับ"><option value="">ครัวเรือน + รายบุคคล</option><option v-for="(l, k) in LEVEL_LABEL" :key="k" :value="k">เฉพาะ{{ l }}</option></select>
        <button type="button" class="btn btn-outline-secondary flex-shrink-0" :class="{ 'border-primary-1 text-primary-1': moreActive }" @click="showMore = !showMore"><FilterIcon class="w-4 h-4 mr-1" /> ตัวกรองอื่น<span v-if="moreActive" class="ml-1">({{ moreActive }})</span></button>
      </div>
      <div v-if="showMore" class="flex flex-col md:flex-row gap-2 mb-3">
        <select v-model="fGroup" class="form-select md:w-56" aria-label="ชุดแบบสอบถาม"><option value="">ทุกชุดแบบสอบถาม</option><option v-for="(l, k) in GROUP_LABEL" :key="k" :value="k">{{ l }}</option></select>
        <select v-model="fStatus" class="form-select md:w-44" aria-label="สถานะ"><option value="">เปิดและปิดใช้งาน</option><option value="on">เปิดใช้งาน</option><option value="off">ปิดใช้งาน</option></select>
        <select v-model="fWelfare" class="form-select md:w-56" aria-label="การเชื่อมสวัสดิการ"><option value="">เชื่อมสวัสดิการแล้วหรือยังก็ได้</option><option value="none">ยังไม่เชื่อมสวัสดิการ</option><option value="some">เชื่อมสวัสดิการแล้ว</option></select>
      </div>
      <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-theme-37 mb-3">
        <span>แสดง {{ filtered.length }} จาก {{ rules.length }} รายการ</span>
        <span v-if="canDrag" class="inline-flex items-center gap-1"><MoveIcon class="w-3.5 h-3.5" /> ลากที่ไอคอนหน้าแถวเพื่อจัดลำดับภายในระดับเดียวกัน</span>
        <button v-else type="button" class="underline" @click="clearFilters">ล้างตัวกรองทั้งหมด</button>
      </div>

      <div v-if="loading" class="py-10 text-center text-theme-37"><LoadingIcon icon="oval" class="w-8 h-8 mx-auto" /> กำลังโหลด…</div>
      <template v-else>
        <section v-for="level in visibleLevels" :key="level" class="mb-6 last:mb-0">
          <div class="flex items-center gap-2 mb-2">
            <component :is="level === 'household' ? 'HomeIcon' : 'UsersIcon'" class="w-4 h-4" style="color:#415A77" />
            <div class="text-sm font-semibold text-gray-800">ระดับ{{ LEVEL_LABEL[level] }} ({{ byLevel[level].length }})</div>
          </div>
          <div class="overflow-x-auto">
            <table class="table table-admin w-full">
              <thead>
                <tr>
                  <th class="w-8"></th>
                  <th class="min-w-[18rem]">ปัญหา</th>
                  <th class="min-w-[20rem]">เกิดจากคำตอบ</th>
                  <th class="w-44">สวัสดิการ (ขั้นที่ 2)</th>
                  <th class="w-20 text-center">ใช้งาน</th>
                  <th class="w-28 text-center">จัดการ</th>
                </tr>
              </thead>
              <tbody :ref="(el) => setLevelEl(level, el)" :data-level="level">
                <tr v-for="r in byLevel[level]" :key="r.rule_id" :data-id="r.rule_id" :class="{ 'opacity-60': r.enabled === false }">
                  <td class="align-middle"><span v-if="canDrag" class="drag-handle inline-flex" title="ลากเพื่อจัดลำดับ"><MoreVerticalIcon class="w-4 h-4 -mr-2" /><MoreVerticalIcon class="w-4 h-4" /></span></td>
                  <td>
                    <div class="font-medium text-gray-800 break-words cursor-pointer hover:underline" @click="openEdit(r)">{{ ruleTitle(r) }}</div>
                    <div class="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-1">
                      <span v-if="r.dimension" class="chip" :style="DIMENSION_STYLE[r.dimension]">{{ r.dimension }}</span>
                      <span v-else class="chip chip-muted">ยังไม่ระบุมิติ</span>
                      <span v-if="isProblemRule(r)" class="chip" :style="SEVERITY_STYLE[r.severity] || SEVERITY_STYLE.monitor">{{ SEVERITY_LABEL[r.severity] || SEVERITY_LABEL.monitor }}</span>
                      <span v-else class="chip chip-muted" title="ไม่ขึ้นเป็นปัญหา ใช้แนะนำสวัสดิการให้ผู้ที่เข้าเงื่อนไข">แนะนำสวัสดิการ</span>
                      <span class="text-xs text-theme-37">{{ r.rule_id }}</span>
                    </div>
                  </td>
                  <td class="text-sm text-gray-700">
                    <ul class="cond-list clamp-4" :title="conditionSentence(r, qByCode)">
                      <li v-for="(c, i) in r.if?.all || []" :key="'a' + i"><span v-if="i > 0" class="cond-and">และ</span>{{ condText(c, qByCode) }}</li>
                      <li v-for="(c, i) in r.if?.any || []" :key="'o' + i"><span class="cond-and">{{ i > 0 ? 'หรือ' : (r.if?.all?.length ? 'และ' : '') }}</span>{{ condText(c, qByCode) }}</li>
                    </ul>
                  </td>
                  <td class="text-sm">
                    <router-link :to="{ name: 'welfare-mapping', query: { rule: r.rule_id } }" class="welfare-link" :class="{ 'welfare-link--none': !usable(r).length }" :title="welfareTitle(r)">
                      <GiftIcon class="w-3.5 h-3.5 flex-shrink-0" />
                      <span v-if="usable(r).length">{{ usable(r).length }} รายการ</span>
                      <span v-else>{{ (r.welfare || []).length ? 'ไม่มีที่ใช้ได้วันนี้' : 'ยังไม่เชื่อม' }}</span>
                      <ChevronRightIcon class="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                    </router-link>
                    <div v-if="expired(r).length" class="text-xs mt-1" style="color:#CE3131">หมดอายุ {{ expired(r).length }} รายการ</div>
                  </td>
                  <td class="text-center align-middle"><input type="checkbox" class="form-check-switch" :checked="r.enabled !== false" @change="toggleEnabled(r, $event)"></td>
                  <td class="text-center align-middle whitespace-nowrap">
                    <button type="button" class="icon-btn icon-btn-edit mr-1" title="แก้ไข" @click="openEdit(r)"><Edit2Icon class="w-4 h-4" /></button>
                    <button type="button" class="icon-btn icon-btn-view mr-1" title="ทำสำเนา" @click="openDuplicate(r)"><CopyIcon class="w-4 h-4" /></button>
                    <button type="button" class="icon-btn icon-btn-del" title="ลบ" @click="remove(r)"><Trash2Icon class="w-4 h-4" /></button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!byLevel[level].length" class="empty-state">ไม่มีรายการในระดับนี้ที่ตรงกับตัวกรอง</div>
          </div>
        </section>
      </template>
    </div>

    <RuleEditor :show="showEditor" :rule="editing" :questions="questions" :defaults="createDefaults" @close="showEditor = false" @saved="onSaved" />
  </WelfareShell>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import WelfareShell from "../components/WelfareShell.vue";
import RuleEditor from "./RuleEditor.vue";
import { welfareApi, apiErrorMessage } from "../api";
import { DIMENSIONS, DIMENSION_STYLE, LEVEL_LABEL, GROUP_LABEL, SEVERITY_LABEL, SEVERITY_STYLE, isProblemRule } from "../constants";
import { ruleTitle, ruleConditions, condText, conditionSentence } from "../ruleText";
import { isUsableToday, periodStatus } from "../period";
import { useSortable, moveItem, revertDom } from "../useSortable";
import { toastSuccess, toastError, confirmDialog, alertError } from "../components/useToast";

const KINDS = [
  { value: "", label: "ทั้งหมด", help: "ทุกรายการ" },
  { value: "problem", label: "ปัญหา", help: "ขึ้นในรายการ “ปัญหาที่ตรวจพบ” บนหน้าครัวเรือน" },
  { value: "welfare", label: "แนะนำสวัสดิการ", help: "ไม่ขึ้นเป็นปัญหา ใช้แนะนำสวัสดิการให้ผู้ที่เข้าเงื่อนไข" },
];

const route = useRoute();
const router = useRouter();
const rules = ref([]);
const questions = ref([]);
const loading = ref(false);
const q = ref("");
const fKind = ref("");
const fLevel = ref("");
const fGroup = ref("");
const fDim = ref("");
const fStatus = ref("");
const fWelfare = ref("");
const showMore = ref(false);
const showEditor = ref(false);
const editing = ref(null);
const levelEls = { household: null, member: null };
const setLevelEl = (level, el) => { levelEls[level] = el; };

const qByCode = computed(() => Object.fromEntries(questions.value.map((x) => [x.code, x])));
const kindOf = (r) => (isProblemRule(r) ? "problem" : "welfare");
const kindCount = computed(() => ({ "": rules.value.length, problem: rules.value.filter(isProblemRule).length, welfare: rules.value.filter((r) => !isProblemRule(r)).length }));
const kindRules = computed(() => rules.value.filter((r) => !fKind.value || kindOf(r) === fKind.value));
const dimCount = computed(() => kindRules.value.reduce((acc, r) => { if (r.dimension) acc[r.dimension] = (acc[r.dimension] || 0) + 1; return acc; }, {}));
const moreActive = computed(() => [fGroup.value, fStatus.value, fWelfare.value].filter(Boolean).length);

// welfare is only summarised here; it is edited on the mapping board
const usable = (r) => (r.welfare || []).filter((w) => w.active !== false && isUsableToday(w.period));
const expired = (r) => (r.welfare || []).filter((w) => periodStatus(w.period) === "expired");
const welfareTitle = (r) => ((r.welfare || []).length ? `${(r.welfare || []).map((w) => w.name).join("\n")}\n\nกดเพื่อจัดการในหน้า ปัญหา → สวัสดิการ` : "กดเพื่อเชื่อมสวัสดิการในหน้า ปัญหา → สวัสดิการ");

const filtered = computed(() => {
  const s = q.value.toLowerCase();
  return kindRules.value.filter((r) => {
    if (fLevel.value && r.level !== fLevel.value) return false;
    if (fGroup.value && r.group !== fGroup.value) return false;
    if (fDim.value && r.dimension !== fDim.value) return false;
    if (fStatus.value === "on" && r.enabled === false) return false;
    if (fStatus.value === "off" && r.enabled !== false) return false;
    if (fWelfare.value === "none" && (r.welfare || []).length) return false;
    if (fWelfare.value === "some" && !(r.welfare || []).length) return false;
    if (s) {
      const hay = [r.rule_id, r.name_th, ...ruleConditions(r).map((c) => `${c.question} ${condText(c, qByCode.value)} ${c.value_desc_th || ""}`)].join(" ").toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
});
const byLevel = computed(() => ({ household: filtered.value.filter((r) => r.level === "household"), member: filtered.value.filter((r) => r.level === "member") }));
const visibleLevels = computed(() => (fLevel.value ? [fLevel.value] : ["household", "member"]));
const canDrag = computed(() => !q.value && !fKind.value && !fDim.value && !moreActive.value);
const clearFilters = () => { q.value = ""; fKind.value = ""; fDim.value = ""; fGroup.value = ""; fStatus.value = ""; fWelfare.value = ""; };

async function load() {
  loading.value = true;
  try {
    const [r, qs] = await Promise.all([welfareApi.rules.list(), questions.value.length ? { items: questions.value } : welfareApi.questions()]);
    rules.value = r.items || [];
    questions.value = qs.items || [];
  } catch (e) {
    toastError(apiErrorMessage(e, "โหลดข้อมูลไม่สำเร็จ"));
  } finally {
    loading.value = false;
    await nextTick();
    attachAll();
  }
}

const { attach, destroyAll } = useSortable({
  handle: ".drag-handle",
  onEnd: async (evt) => {
    const level = evt.from && evt.from.dataset.level;
    revertDom(evt);
    if (!level || evt.oldIndex === evt.newIndex || !canDrag.value) return;
    const list = byLevel.value[level];
    const ids = list.map((r) => r.rule_id);
    moveItem(ids, evt.oldIndex, evt.newIndex);
    // apply the new order to the master list (sort_order is per level)
    const idx = Object.fromEntries(ids.map((id, i) => [id, i]));
    rules.value.forEach((r) => { if (idx[r.rule_id] !== undefined) r.sort_order = idx[r.rule_id]; });
    rules.value.sort((a, b) => (a.level === b.level ? (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9) : 0));
    try {
      await welfareApi.rules.reorder(ids);
      toastSuccess("บันทึกลำดับแล้ว");
    } catch (e) {
      toastError(apiErrorMessage(e, "บันทึกลำดับไม่สำเร็จ"));
      await load();
    }
  },
});
const attached = new Set();
function attachAll() {
  for (const level of ["household", "member"]) {
    const el = levelEls[level];
    if (el && !attached.has(el)) { attach(el, { disabled: !canDrag.value }); attached.add(el); }
  }
}
watch(visibleLevels, async () => { await nextTick(); attachAll(); });
watch(canDrag, async () => { destroyAll(); attached.clear(); await nextTick(); attachAll(); });

// a new problem starts from whatever the admin is looking at (dimension card / level / kind)
const createDefaults = computed(() => ({ dimension: fDim.value || null, level: fLevel.value || null, kind: fKind.value || "problem" }));
const openCreate = () => { editing.value = null; showEditor.value = true; };
const openEdit = (r) => { editing.value = r; showEditor.value = true; };
const openDuplicate = (r) => { editing.value = { ...r, _duplicate: true }; showEditor.value = true; };
async function onSaved(item, { goMapping } = {}) {
  showEditor.value = false;
  if (goMapping && item) { router.push({ name: "welfare-mapping", query: { rule: item.rule_id } }); return; }
  await load();
}

async function toggleEnabled(r, evt) {
  const enabled = evt.target.checked;
  try {
    await welfareApi.rules.patch(r.rule_id, { enabled });
    r.enabled = enabled;
    toastSuccess(enabled ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว");
  } catch (e) {
    evt.target.checked = !enabled;
    toastError(apiErrorMessage(e));
  }
}
async function remove(r) {
  const n = (r.welfare || []).length;
  const ok = await confirmDialog({ title: "ยืนยันลบ", text: `${r.rule_id} · ${ruleTitle(r)}${n ? ` (การเชื่อมสวัสดิการ ${n} รายการจะถูกลบไปด้วย)` : ""}`, confirmText: "ลบ" });
  if (!ok) return;
  try {
    await welfareApi.rules.remove(r.rule_id);
    toastSuccess("ลบแล้ว");
    await load();
  } catch (e) {
    alertError(apiErrorMessage(e));
  }
}

onMounted(async () => {
  await load();
  // deep link from the mapping board: /admin/welfare/rules?edit=HOUSE-R003 (or ?new=1)
  const editId = route.query.edit;
  if (editId) {
    const r = rules.value.find((x) => x.rule_id === editId);
    if (r) openEdit(r);
    router.replace({ query: {} });
  } else if (route.query.new) {
    openCreate();
    router.replace({ query: {} });
  }
});
</script>

<style scoped>
.dim-card { display: flex; flex-direction: column; align-items: flex-start; gap: 0.1rem; text-align: left; padding: 0.6rem 0.8rem; border-radius: 0.5rem; border: 1px solid #e2e8f0; border-top: 3px solid var(--dim); background: #fff; transition: background-color 0.12s ease, box-shadow 0.12s ease; }
.dim-card:hover { box-shadow: 0 2px 8px rgba(65, 90, 119, 0.15); }
.dim-card--on { background: var(--dim-bg, #eef1f6); border-color: var(--dim); box-shadow: inset 0 0 0 1px var(--dim); }
.dim-card-count { font-size: 1.4rem; font-weight: 600; line-height: 1.2; color: var(--dim); }
.dim-card-label { font-size: 0.85rem; color: #4a5568; }
.seg { display: inline-flex; border: 1px solid #cbd5e0; border-radius: 0.375rem; overflow: hidden; }
.seg button { padding: 0.5rem 0.8rem; font-size: 0.85rem; background: #fff; color: #4a5568; white-space: nowrap; }
.seg button + button { border-left: 1px solid #cbd5e0; }
.seg button:hover { background: #f7f9fb; }
.seg button.seg--on { background: #415A77; color: #fff; }
.cond-list { list-style: none; padding: 0; margin: 0; line-height: 1.5; }
.cond-list li { display: inline; }
.cond-list li + li::before { content: " "; }
.cond-and { display: inline-block; font-size: 0.7rem; font-weight: 600; color: #415A77; background: #eef1f6; border-radius: 9999px; padding: 0 0.4rem; margin-right: 0.3rem; }
.cond-and:empty { display: none; }
.clamp-4 { display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
.welfare-link { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.25rem 0.55rem; border-radius: 9999px; font-size: 0.78rem; font-weight: 500; color: #1f6b45; background: #e6f5ec; border: 1px solid #b7e1c8; white-space: nowrap; }
.welfare-link:hover { background: #d5eedf; }
.welfare-link--none { color: #B36B00; background: #fdf3df; border-color: #f0dcae; }
.welfare-link--none:hover { background: #fbe9c4; }
</style>
